import React, { useMemo, useState } from 'react'
import {
  BundlePlanBoxStyled,
  BundlePlanBoxProduct,
  BundlePlanBoxProductRow,
  BundlePlanBoxTitle,
  BundlePlanBoxProductTitle,
  BundlePlanCustomPricing,
  BundlePlanBoxBilledAnnually,
} from 'Features/pricing/components/BundlePlanBox/BundlePlanBox.styled'
import {
  BundlePlanBoxButtonAndPrice,
  PlanBoxAmount,
  PlanBoxCurrency,
} from 'Features/pricing/components/PlanBox/PlanBox.styled'
import { Button, useResponsive } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { PLAN_TYPES, PlanTypes } from 'Types/PlansTypes'
import { Plan } from 'Features/pricing/types/Plan'
import { CurrenciesTypes } from 'Types/Currencies'
import { getAmountWithCurrency } from 'Utils/currencyHelper'

import { CSS } from '@stitches/react'

import { ScheduleCallModal } from 'Features/ScheduleCallModal/ScheduleCallModal'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import {
  CrossSellPlanBoxSeparator,
  PricingPlanBoxPriceRow,
} from 'Features/CrossSell/CrosSellPricing/CrossSellPlansBox/CrossSellPlansBox.styled'

import { CrossSellPricingConfirmationModal } from '../../CrosSellPricing/CrossSellPricingConfirmationModal/CrossSellPricingConfirmationModal'
import { showKiloOrMillion } from 'Utils/number'
import { PRODUCT_TYPES, ProductTypesWithoutBundle } from 'Types/ProductTypes'
import { PlansDetailsType, useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'

import { MODIFY_PLANS_PAGES_TYPES } from 'Types/ModifyPlanPagesTypes'
import { botMitigationPlanSizes, paidMarketingPlanSizes } from 'Features/pricing/models/planSizes'
import { ModifyPlanBoxSummary } from '../ModifyPlanBoxSummary/ModifyPlanBoxSummary'
import { useGetCheckoutSummary } from 'Features/CrossSell/CrosSellPricing/api/useGetCheckoutSummary'
import { CheckoutSummaryResponseData } from 'Types/CheckoutSummaryResponseType'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import { PAYMENT_METHOD } from 'Types/PaymentMethod'

type ModifyPlanBoxProps = {
  origPlan: PlansDetailsType | undefined
  productA: ProductTypesWithoutBundle | undefined
  productB?: ProductTypesWithoutBundle | undefined
  planA: PlanTypes | undefined
  planB: PlanTypes | undefined
  visitsA: number
  visitsB: number | undefined
  plansDb: Array<Plan>
  currency: CurrenciesTypes
  billingCycleInMonths: number
  css?: CSS
  source: string
}

export const ModifyPlanBox = ({
  planA,
  planB,
  visitsA,
  visitsB,
  currency,
  plansDb,
  css,
  productA,
  productB,
  origPlan,
  billingCycleInMonths,
  source,
}: ModifyPlanBoxProps) => {
  const { t } = useTranslation('pricing')
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const { findPlan } = useGetPlan()
  const { isLaptop } = useResponsive()
  const { isInstallmentsSubscription } = useUserStore()
  const isTrialSubscription = useIsTrialSubscription()
  const { paymentMethod } = useUserStore()
  const wireTransfer = paymentMethod === PAYMENT_METHOD.WIRE_TRANSFER
  const { data: details } = useGetPlansDetails()

  const addonProductIdsArray = details?.plans
    ?.filter((product) => product.type === 'Addon')
    .map((product) => product.id)

  const plans: Plan[] = []
  const newPlanA = findPlan(
    plansDb,
    planA || 'Core',
    currency,
    productA || PRODUCT_TYPES.PAID_MARKETING,
    visitsA,
    billingCycleInMonths
  )
  if (newPlanA) {
    plans.push(newPlanA)
  }
  const newPlanB = findPlan(
    plansDb,
    planB || 'Core',
    currency,
    productB || PRODUCT_TYPES.BOT_ZAPPING,
    visitsB || 0,
    billingCycleInMonths
  )
  if (newPlanB) {
    plans.push(newPlanB)
  }
  const queryData = useGetCheckoutSummary(
    planB ? [newPlanA?.id || 0, newPlanB?.id || 0] : [newPlanA?.id || 0]
  )
  const summaryData: CheckoutSummaryResponseData | undefined = useMemo(() => {
    if (queryData && queryData.data) return queryData.data.data
  }, [queryData])

  const handleClick = () => {
    if (isCustomPricing || wireTransfer) setShowScheduleModal(true)
    else setShowConfirmationModal(true)
  }

  const getPlanName = () => {
    if (visitsB) return t('modify_plan.paid_marketing')
    return t(
      origPlan?.plans[0].product.toLowerCase() === PRODUCT_TYPES.PAID_MARKETING.toLowerCase()
        ? 'modify_plan.paid_marketing'
        : 'modify_plan.bot_mitigation'
    )
  }

  const isCustomPricing = useMemo(() => {
    return (
      (origPlan?.plans[0].product.toLowerCase() === PRODUCT_TYPES.PAID_MARKETING.toLowerCase() &&
        visitsA > paidMarketingPlanSizes[4]) ||
      (origPlan?.plans[0].product.toLowerCase() === PRODUCT_TYPES.BOT_ZAPPING.toLowerCase() &&
        visitsA > botMitigationPlanSizes[4]) ||
      (visitsB && visitsB > botMitigationPlanSizes[4]) ||
      planA === PLAN_TYPES.ENTERPRISE ||
      planB === PLAN_TYPES.ENTERPRISE
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitsA, visitsB, planA, planB])

  const isCredited = useMemo(() => {
    return origPlan && origPlan?.totalAmount > (newPlanA?.amount || 0) + (newPlanB?.amount || 0)
  }, [origPlan, newPlanA, newPlanB])

  const amountToPay = summaryData?.netAmountToPay
    ? Math.round(Math.abs(summaryData.netAmountToPay)).toLocaleString()
    : '0'

  const modalHeader = isTrialSubscription
    ? t('modify_plan.conformation_modal_title_during_trial')
    : t('modify_plan.confirm_new_plan')

  const modalBody = isTrialSubscription
    ? t('modify_plan.conformation_modal_body_during_trial', {
        amount: `${getAmountWithCurrency(currency, undefined)}${amountToPay} `,
      })
    : t('modify_plan.confirm_message', {
        planName: getPlanName(),
        action: t(isCredited ? 'modify_plan.credited' : 'modify_plan.charged'),
        amount: `${getAmountWithCurrency(currency, undefined)}${amountToPay} `,
      })

  const currentPlan = summaryData && !('netAmountToPay' in summaryData)
  const planBoxTitle =
    (summaryData && !currentPlan) || isCustomPricing
      ? t('cross-sell.cross_sale_pricing.your_new_plan')
      : t('modify_plan.current_plan')

  const showSummaryDetails = newPlanA && summaryData && !currentPlan && !wireTransfer

  const paymentText: string = (() => {
    let text = ''
    if (isTrialSubscription && summaryData && summaryData?.netAmountToPay > 0) {
      text = t('modify_plan.amount_to_pay_trial')
    } else if (summaryData && summaryData?.netAmountToPay > 0) {
      text = t('modify_plan.amount_due')
    } else if (summaryData && summaryData?.netAmountToPay < 0) {
      text = t('modify_plan.amount_credited')
    }
    return text
  })()

  const showCtaBtn = (!currentPlan && wireTransfer) || !wireTransfer

  return (
    <>
      <BundlePlanBoxStyled css={css}>
        <BundlePlanBoxTitle>{planBoxTitle}</BundlePlanBoxTitle>
        <BundlePlanBoxProduct>
          <BundlePlanBoxProductTitle>{getPlanName()}</BundlePlanBoxProductTitle>
          <BundlePlanBoxProductRow>
            {t('bundles.plan_type', { plan: planA })}
          </BundlePlanBoxProductRow>
          <BundlePlanBoxProductRow>
            {t('bundles.yearly_visits', {
              visits:
                visitsA * 1000 > (newPlanA?.visitsAllowed || 0)
                  ? t('paid_marketing.million')
                  : showKiloOrMillion(newPlanA?.visitsAllowed || 0),
            })}
          </BundlePlanBoxProductRow>
        </BundlePlanBoxProduct>
        {planB && visitsB && (
          <BundlePlanBoxProduct>
            <BundlePlanBoxProductTitle>{t('bundles.bot_mitigation')}</BundlePlanBoxProductTitle>
            <BundlePlanBoxProductRow>
              {t('bundles.plan_type', { plan: planB })}
            </BundlePlanBoxProductRow>
            <BundlePlanBoxProductRow>
              {t('bundles.yearly_visits', {
                visits:
                  visitsB * 1000 > (newPlanB?.visitsAllowed || 0)
                    ? t('paid_marketing.million')
                    : showKiloOrMillion(newPlanB?.visitsAllowed || 0),
              })}
            </BundlePlanBoxProductRow>
          </BundlePlanBoxProduct>
        )}

        {!isInstallmentsSubscription && <CrossSellPlanBoxSeparator />}
        <BundlePlanBoxButtonAndPrice>
          {!isCustomPricing && showSummaryDetails && (
            <>
              <PricingPlanBoxPriceRow>
                <PlanBoxCurrency>{getAmountWithCurrency(currency, undefined)}</PlanBoxCurrency>
                <PlanBoxAmount>{amountToPay}</PlanBoxAmount>
              </PricingPlanBoxPriceRow>
              <BundlePlanBoxBilledAnnually>{paymentText}</BundlePlanBoxBilledAnnually>
            </>
          )}
          {isCustomPricing && (
            <BundlePlanCustomPricing> {t('bundles.custom_pricing')}</BundlePlanCustomPricing>
          )}

          {showSummaryDetails && <ModifyPlanBoxSummary plans={plans} />}
          {showCtaBtn && (
            <Button
              onClick={handleClick}
              disabled={(!summaryData && !isCustomPricing) || currentPlan}
              css={{
                width: isLaptop ? '85%' : '226px',
                maxWidth: isLaptop ? '85%' : '226px',
                height: isLaptop ? '30px' : '42px',
                maxHeight: '42px',
                marginTop: isLaptop ? '10px' : '20px',
              }}
              text={isCustomPricing || wireTransfer ? t('lets_talk') : t('modify_plan.modify_plan')}
            />
          )}
        </BundlePlanBoxButtonAndPrice>
      </BundlePlanBoxStyled>
      {showConfirmationModal && (
        <CrossSellPricingConfirmationModal
          pageType={MODIFY_PLANS_PAGES_TYPES.MODIFY_PLAN}
          bundle={!!planB}
          plans={[newPlanA?.id || 0, newPlanB?.id || 0, ...(addonProductIdsArray as number[])]}
          title={modalHeader}
          description={modalBody}
          notice={isCredited ? t('modify_plan.notice') : ''}
          isDowngrade={isCredited}
          onOpenChange={() => setShowConfirmationModal(false)}
          productType={productA}
          billingCycleInMonths={billingCycleInMonths}
          amountToPay={getAmountWithCurrency(
            currency,
            summaryData ? Math.round(Math.abs(summaryData.netAmountToPay)) : 0
          )}
          noNewProduct
        />
      )}
      {showScheduleModal && (
        <ScheduleCallModal
          isOpen
          onOpenChange={() => setShowScheduleModal(false)}
          source={source}
        />
      )}
    </>
  )
}
